import { createContext, useContext, useMemo } from "react";
import { DEFAULT_SPORT, normalizeSportId } from "../domain/sport/sportTypes";
import { resolveSportModule } from "./registry";

const SportContext = createContext(null);

/**
 * Resolve sport module once per session shell. Children use useSport().
 */
export function SportProvider({ sportId, children }) {
  const value = useMemo(() => {
    const id = normalizeSportId(sportId || DEFAULT_SPORT);
    const mod = resolveSportModule(id);
    const engine = mod?.createMatchEngine?.() ?? null;
    const config = mod?.createMatchConfiguration?.() ?? null;
    const CourtRenderer = mod?.createCourtRenderer?.() ?? null;
    const RefereeExtras = mod?.createRefereeExtras?.() ?? null;
    const isPlayable = Boolean(mod?.isPlayable);
    return {
      sportId: mod?.id || id,
      module: mod,
      engine,
      config,
      CourtRenderer,
      RefereeExtras,
      isPlayable,
      scoringSystem:
        config?.getScoringSystem?.() || engine?.scoringSystem || null,
    };
  }, [sportId]);

  return (
    <SportContext.Provider value={value}>{children}</SportContext.Provider>
  );
}

/**
 * @returns {{
 *   sportId: string,
 *   module: import("./SportModule").SportModule | null,
 *   engine: import("./contracts/MatchEngine").MatchEngine | null,
 *   config: object | null,
 *   CourtRenderer: import("react").ComponentType<any> | null,
 *   RefereeExtras: import("react").ComponentType<any> | null,
 *   isPlayable: boolean,
 *   scoringSystem: string | null,
 * }}
 */
export function useSport() {
  const ctx = useContext(SportContext);
  if (!ctx) {
    // Safe fallback outside provider (tests / partial mounts)
    const id = DEFAULT_SPORT;
    const mod = resolveSportModule(id);
    return {
      sportId: id,
      module: mod,
      engine: mod?.createMatchEngine?.() ?? null,
      config: mod?.createMatchConfiguration?.() ?? null,
      CourtRenderer: mod?.createCourtRenderer?.() ?? null,
      RefereeExtras: mod?.createRefereeExtras?.() ?? null,
      isPlayable: Boolean(mod?.isPlayable),
      scoringSystem: "rally",
    };
  }
  return ctx;
}

export { SportContext };
